/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { fetchPeTm, PE_TM_QUERY_KEY } from '../../../../../app/pages/IPList/ipQueries'
import { KTIcon } from '../../../../helpers'
import { Column, useFilters, useGlobalFilter, useSortBy, useTable } from 'react-table'
import moment from 'moment'
import { DatePicker } from 'antd'
import { useLayout } from '../../../../layout/core'

type Props = {
  className: string
}

type columnType = {
  [x: string]: any
  name: string
  created_at: string
  uuid: string
}

const TablesWidgetIPLogs: React.FC<Props> = ({ className }) => {
  const intl = useIntl()
  const { setUuidOfVendorForIpList } = useLayout()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data, isLoading } = useQuery(
    [PE_TM_QUERY_KEY, { page, pageSize, debouncedSearch, fromDate, toDate }],
    () => fetchPeTm({
      page,
      page_size: pageSize,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    }),
    { keepPreviousData: true }
  )

  const results: any[] = data?.results ?? []
  const totalCount = data?.count ?? 0
  const pageCount = Math.ceil(totalCount / pageSize) || 1

  const columns: ReadonlyArray<Column<any>> = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'TABLE.SL_NO', defaultMessage: 'S.No' }),
        id: 'serial-number',
        Cell: ({ row }) => (page - 1) * pageSize + row.index + 1,
      } as unknown as Column<columnType>,
      {
        id: 'DATE',
        Header: intl.formatMessage({ id: 'TABLE.DATE', defaultMessage: 'DATE' }),
        accessor: 'created_at',
        Cell: ({ value }) => (
          <>
            <span>{moment(value).format('DD/MM/YYYY')}</span>
            <span className='d-block text-muted fw-semibold'>{moment(value).format('LT')}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.VENDOR_NAME', defaultMessage: 'Vendor Name' }),
        accessor: 'name',
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.VENDOR_TYPE', defaultMessage: 'Vendor Type' }),
        accessor: 'node.node_name',
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.VENDOR_ID', defaultMessage: 'Vendor ID' }),
        accessor: 'node.node_id',
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.TOTAL_IP', defaultMessage: 'TOTAL IP' }),
        accessor: 'ip_count.total',
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.WHITELISTED', defaultMessage: 'Whitelisted' }),
        accessor: 'ip_count.whitelisted',
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.BLACKLISTED', defaultMessage: 'Blacklisted' }),
        accessor: 'ip_count.blacklisted',
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.ACTION', defaultMessage: 'ACTION' }),
        id: 'col-action',
        accessor: 'uuid',
        Cell: ({ value }) => (
          <button
            className='btn h-35px btn-icon btn btn-light-info px-10'
            onClick={() => setUuidOfVendorForIpList(value)}
            id='kt_drawer_ip_list_toggle'
          >
            {intl.formatMessage({ id: 'COMMON.VIEW', defaultMessage: 'View' })}
          </button>
        ),
      },
    ],
    [page, pageSize, intl]
  )

  const ipTable = useTable<any>(
    {
      columns,
      data: results,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetSortBy: false,
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  const { RangePicker } = DatePicker

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage({ id: 'IPLIST.TITLE', defaultMessage: 'PE-TM Details' })}</span>
          <span className='text-muted fw-semibold fs-7'>{intl.formatMessage({ id: 'IPLIST.TOTAL_VENDOR', defaultMessage: 'Total {count} Vendor' }, { count: totalCount })}</span>
        </h3>
        <div className='d-flex align-items-center'>
          <RangePicker
            className='bg-secondary-custom text-white border-0 shadow-none me-2'
            style={{ height: '35px' }}
            onChange={(_, val) => {
              setFromDate(val[0] ?? '')
              setToDate(val[1] ?? '')
              setPage(1)
            }}
            disabledDate={(current) => current && current.valueOf() > Date.now()}
          />
          <div className='d-flex align-items-center position-relative my-1 ms-2'>
            <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
            <input
              type='text'
              className='form-control form-control-solid form-select-sm w-150px ps-9'
              placeholder={intl.formatMessage({ id: 'IPLIST.SEARCH_VENDOR', defaultMessage: 'Search Vendor' })}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='table-responsive'>
          <table
            {...ipTable.getTableProps()}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          >
            <thead>
              {ipTable.headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const headerValue = column.render('Header')
                    if (!headerValue) return null
                    return (
                      <th
                        className={column.id === 'col-action' ? 'w-200px' : ''}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >
                        <span className='text-gray-400 fs-7'>{headerValue}</span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <KTIcon iconName='down' className='text-gray ms-1' />
                          ) : (
                            <KTIcon iconName='up' className='text-gray ms-1' />
                          )
                        ) : (
                          ''
                        )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...ipTable.getTableBodyProps()}>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className='text-center py-6'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : ipTable.rows.length > 0 ? (
                ipTable.rows.map((row) => {
                  ipTable.prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td className='fw-bold' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={10} className='text-center'>
                    <span className='fw-bold'>No data found.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className='d-flex justify-content-between align-items-center mt-5'>
            <div>
              <select
                className='form-select form-select-sm form-select-solid'
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1) }}
              >
                {[10, 25, 50, 100].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </div>
            <div>
              <span>
                Page{' '}
                <strong>
                  {page} of {pageCount}
                </strong>
              </span>
            </div>
            <div>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                <KTIcon iconName='left' className='text-gray fs-1 me-5' />
              </button>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pageCount}
              >
                <KTIcon iconName='right' className='text-gray fs-1 me-5' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { TablesWidgetIPLogs }
